import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { Button } from '$partials/ui';
import { Dispatcher as IconDispatcher } from '$partials/icon';
import { Dispatcher as ItemDispatcher } from '$partials/listings';
import { useLanguage } from '$contexts/language.jsx';
import Utils from '$utilities';
import { StaticTitle } from '$partials/layout/side-titles.jsx';

/* =========================================================================  */
/* Component(s) */
/* =========================================================================  */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const ExploreJobAjax = ({
    sideTitle = null,
    title     = null
}) => {
    // misc
    const ITEMS_PER_PAGE = 12;

    // state
    const [listingFilterAvailableCategory, setListingFilterAvailableCategory] = useState([]);
    const [listingFilterAvailableIndustry, setListingFilterAvailableIndustry] = useState([]);
    const [listingFilterAvailableLocation, setListingFilterAvailableLocation] = useState([]);
    const [listingFilterCategory, setListingFilterCategory] = useState([]);
    const [listingFilterIndustry, setListingFilterIndustry] = useState([]);
    const [listingFilterLocation, setListingFilterLocation] = useState([]);
    const [listingItemsFetched, setListingItemsFetched] = useState([]);
    const [listingItems, setListingItems] = useState([]);
    const [listingPage, setListingPage] = useState(1);
    const [listingTotalPages, setListingTotalPages] = useState();
    const [showFilterUI, setShowFilterUI] = useState(false);

    // methods
    const handleFilterCategoryClick = (category) => {
        if (listingFilterCategory.includes(category) || !category) {
            // remove
            // const idx = listingFilterCategory.indexOf(category);
            // setListingFilterCategory([
            //     ...listingFilterCategory.slice(0, idx),
            //     ...listingFilterCategory.slice(idx + 1)
            // ]);

            setListingFilterCategory([]);
        } else {
            // add
            // setListingFilterCategory([...listingFilterCategory, category]);

            setListingFilterCategory([category]);
        }
    };

    const handleFilterIndustryClick = (industry) => {
        if (listingFilterIndustry.includes(industry) || !industry) {
            // remove
            // const idx = listingFilterIndustry.indexOf(industry);
            // setListingFilterIndustry([
            //     ...listingFilterIndustry.slice(0, idx),
            //     ...listingFilterIndustry.slice(idx + 1)
            // ]);

            setListingFilterIndustry([]);
        } else {
            // add
            // setListingFilterIndustry([...listingFilterIndustry, industry]);

            setListingFilterIndustry([industry]);
        }
    };

    const handleFilterLocationClick = (location) => {
        if (listingFilterLocation.includes(location) || !location) {
            // remove
            // const idx = listingFilterLocation.indexOf(location);
            // setListingFilterLocation([
            //     ...listingFilterLocation.slice(0, idx),
            //     ...listingFilterLocation.slice(idx + 1)
            // ]);

            setListingFilterLocation([]);
        } else {
            // add
            // setListingFilterLocation([...listingFilterLocation, location]);

            setListingFilterLocation([location]);
        }
    };

    const areFiltersSelected = () => {
        return (
            listingFilterCategory.length > 0 ||
            listingFilterIndustry.length > 0 ||
            listingFilterLocation.length > 0
        );
    };

    // hooks
    const { translate: t } = useLanguage();

    useEffect(() => {
        const fetchData = async () => {
            // get job items
            const response = await fetch('/api/v1/joblist/jobs', {
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': Utils.cookies.getItem('crumb')
                },
                method: 'GET',
            });

            if (response?.status !== 200) {
                throw new Error(response?.statusText);
            }

            // get json response
            const responseJson = await response.json();

            // get and store available filters and items
            const _listingFilterAvailableCategory = [];
            const _listingFilterAvailableIndustry = [];
            const _listingFilterAvailableLocation = [];
            const _listingItemsFetched = [];
            responseJson.results?.forEach((item) => {
                // add typename to items for item dispatcher
                item.__typename = 'Job';

                _listingFilterAvailableCategory.push(...item?.jobCategories);
                _listingFilterAvailableLocation.push(item?.geo_city);
                _listingFilterAvailableIndustry.push(...item?.industries);
                _listingItemsFetched.push(item);
            });

            // remove duplicate items in arrays using Sets
            setListingFilterAvailableCategory([...new Set(_listingFilterAvailableCategory)].sort());
            setListingFilterAvailableIndustry([...new Set(_listingFilterAvailableIndustry)].sort());
            setListingFilterAvailableLocation([...new Set(_listingFilterAvailableLocation)].sort());

            setListingItemsFetched(_listingItemsFetched);
            setListingTotalPages(Math.ceil(_listingItemsFetched.length / ITEMS_PER_PAGE) || 1);

            setListingItems(_listingItemsFetched);
        };

        fetchData();
    }, []);

    useEffect(() => {
        const _listingItems = [];

        listingItemsFetched.some(item => {
            let categoryMatch = false;
            let industryMatch = false;
            let locationMatch = false;

            // filter items if any category specified
            if (listingFilterCategory.length > 0) {
                categoryMatch = item?.jobCategories.some(job => {
                    return listingFilterCategory.includes(job);
                });
            } else {
                categoryMatch = true;
            }

            // filter items if any industry specified
            if (listingFilterIndustry.length > 0) {
                industryMatch = item?.industries.some(industry => {
                    return listingFilterIndustry.includes(industry);
                });
            } else {
                industryMatch = true;
            }

            // filter items if any location specified
            if (listingFilterLocation.length > 0) {
                locationMatch = listingFilterLocation.includes(item?.geo_city);
            } else {
                locationMatch = true;
            }

            // if all filters match, keep item in listing
            if (categoryMatch && industryMatch && locationMatch) {
                _listingItems.push(item);
            }
        });

        setListingItems(_listingItems);
        setListingTotalPages(Math.ceil(_listingItems.length / ITEMS_PER_PAGE) || 1);
        setListingPage(1);
    }, [listingFilterCategory, listingFilterIndustry, listingFilterLocation]);

    // render
    return (
        <section className='module-explore-job'>
            <div className='pr-gutter mxn-gutter'> {/* outer grid gutter */}
                <div className='flex flex-wrap'>
                    {/* side title */}
                    <div className='absolute top-0 md-relative md-col-2'>
                        {sideTitle && (
                            <div className='px-gutter'>
                                <p className='type-style-5 side-title'>
                                    <StaticTitle title={sideTitle} />
                                </p>
                            </div>
                        )}
                    </div>

                    {/* content / title */}
                    <div className='col-11 sm-col-8 md-col-7'>
                        <div className='ml9 sm-ml9 md-m0'>
                            <div className='px-gutter'>
                                <div
                                    className='rich-text type-style-1'
                                    dangerouslySetInnerHTML={{__html: title}} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* content: desktop */}
            <div className='pr-gutter mxn-gutter'> {/* outer grid gutter */}
                <div className='flex flex-wrap'>
                    {/* side title */}
                    <div className='absolute top-0 md-relative md-col-2'>
                        {(!title && sideTitle) && (
                            <div className='px-gutter'>
                                <p className='type-style-5 side-title side-title--no-top'>
                                    <StaticTitle title={sideTitle} />
                                </p>
                            </div>
                        )}
                    </div>

                    {/* content */}
                    <div className='col-12 sm-col-12 md-col-10'>
                        <div className='ml9 sm-ml9 md-ml0'>
                            {listingItemsFetched?.length > 0 && (
                                <>
                                    {/* filters */}
                                    <div className='mxn-gutter'> {/* for yellow background */}
                                        <div className={`px-gutter mt9 sm-mt9 md-mt12 py5${(showFilterUI || areFiltersSelected()) ? ' bg-lemon-yellow-crayola' : null}`}>
                                            <div className='px-gutter'> {/* to offset the negative margin for yellow background */}
                                                <div className={(!showFilterUI && areFiltersSelected()) ? null : 'border-bottom border-black'}>
                                                    <div className='flex justify-between items-baseline'>
                                                        <div className='col-9 sm-col-10 md-col-10'>
                                                            <p className='inline-block pb2'>
                                                                {/* filter title */}
                                                                <span className='capitalise'>{t('Showing')} </span>

                                                                <span>
                                                                    {listingPage === listingTotalPages ? ` ${`${t('all')} ${listingItems.length}` } ` : ` ${listingPage * ITEMS_PER_PAGE} ${t('of')} ${listingItems.length} `}
                                                                </span>

                                                                <span className={areFiltersSelected() ? null : 'capitalise'}> {t('positions')} </span>

                                                                {areFiltersSelected() && (
                                                                    <span className='mr4'> {t('for')}: </span>
                                                                )}
                                                            </p>

                                                            {/* selected filters */}
                                                            <div className={`inline-block type-style-3 pb2${showFilterUI ? '' : ' hans-purple'}`}>
                                                                {listingFilterLocation.map((location, idx) => {
                                                                    return (
                                                                        <Button
                                                                            action={() => handleFilterLocationClick()}
                                                                            className={`${showFilterUI ? 'button mr2 ' : 'inline-block mr5 '}type-style-6 mb2`}
                                                                            key={`location-${idx}`}>
                                                                            <span>
                                                                                {location}
                                                                            </span>

                                                                            <IconDispatcher
                                                                                className='icon-inline'
                                                                                name='IconCross' />
                                                                        </Button>
                                                                    );
                                                                })}

                                                                {listingFilterCategory.map((category, idx) => {
                                                                    return (
                                                                        <Button
                                                                            action={() => handleFilterCategoryClick()}
                                                                            className={`${showFilterUI ? 'button mr2 ' : 'inline-block mr5 '}type-style-6 mb2`}
                                                                            key={`category-${idx}`}>
                                                                            <span>
                                                                                {category}
                                                                            </span>

                                                                            <IconDispatcher
                                                                                className='icon-inline'
                                                                                name='IconCross' />
                                                                        </Button>
                                                                    );
                                                                })}

                                                                {listingFilterIndustry.map((industry, idx) => {
                                                                    return (
                                                                        <Button
                                                                            action={() => handleFilterIndustryClick()}
                                                                            className={`${showFilterUI ? 'button mr2 ' : 'inline-block mr5 '}type-style-6 mb2`}
                                                                            key={`industry-${idx}`}>
                                                                            <span>
                                                                                {industry}
                                                                            </span>

                                                                            <IconDispatcher
                                                                                className='icon-inline'
                                                                                name='IconCross' />
                                                                        </Button>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>

                                                        {/* ui controls */}
                                                        <div className='flex'>
                                                            {areFiltersSelected() && (
                                                                <Button
                                                                    action={() => {
                                                                        handleFilterLocationClick();
                                                                        handleFilterCategoryClick();
                                                                        handleFilterIndustryClick();
                                                                    }}
                                                                    className='sm-show mr7'>
                                                                    <span className='capitalise'>
                                                                        {t('Clear')}
                                                                    </span>
                                                                </Button>
                                                            )}

                                                            <Button
                                                                action={() => { setShowFilterUI(!showFilterUI); }}
                                                                className='mrn4'>
                                                                <span className='capitalise'>
                                                                    {t('Filter')}
                                                                </span>

                                                                {showFilterUI ? <IconDispatcher name='IconChevronUp' /> : <IconDispatcher name='IconChevronDown' />}
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* available filters */}
                                                {showFilterUI && (
                                                    <div className='flex flex-wrap mxn-gutter mb9 mt7'>
                                                        <div className='col-12 sm-col-12 md-col-5'>
                                                            <div className='px-gutter'>
                                                                <h3 className='type-style-3 mb4 pb2 border-bottom border-black capitalise'>
                                                                    {t('Location')}
                                                                </h3>

                                                                <ul className='list-reset flex flex-wrap'>
                                                                    {listingFilterAvailableLocation.map((location, idx) => {
                                                                        return (
                                                                            <li key={idx}>
                                                                                <Button
                                                                                    action={() => handleFilterLocationClick(location)}
                                                                                    className={`button${listingFilterLocation.includes(location) ? ' button--active ' : ' '}mb2 mr2`}>
                                                                                    {location}
                                                                                </Button>
                                                                            </li>
                                                                        );
                                                                    })}
                                                                </ul>
                                                            </div>
                                                        </div>

                                                        <div className='md-show md-col-2'/>

                                                        <div className='col-12 sm-col-12 md-col-5'>
                                                            <div className='mt7 sm-mt7 md-mt0 px-gutter'>
                                                                <h3 className='type-style-3 mb4 pb2 border-bottom border-black capitalise'>
                                                                    {t('Field')}
                                                                </h3>

                                                                <ul className='list-reset flex flex-wrap'>
                                                                    {listingFilterAvailableCategory.map((category, idx) => {
                                                                        return (
                                                                            <li key={idx}>
                                                                                <Button
                                                                                    action={() => handleFilterCategoryClick(category)}
                                                                                    className={`button${listingFilterCategory.includes(category) ? ' button--active ' : ' '}mb2 mr2`}>
                                                                                    {category}
                                                                                </Button>
                                                                            </li>
                                                                        );
                                                                    })}
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* listing */}
                                    <section
                                        aria-label='Jobs'
                                        className='flex flex-wrap'
                                        role='feed'>
                                        {listingItems?.map((item, idx) => {
                                            if ((idx + 1) > (ITEMS_PER_PAGE * listingPage)) {
                                                return null;
                                            }

                                            return (
                                                <React.Fragment key={idx}>
                                                    <article
                                                        aria-label={item?.externalPostingName}
                                                        aria-posinset={idx}
                                                        aria-setsize={listingItems.length}
                                                        className='col-12 sm-col-6 md-col-4'
                                                        tabIndex={-1}>
                                                        <div className='px-gutter pt7 sm-pt7 md-pt9'>
                                                            <ItemDispatcher
                                                                item={item}
                                                                key={idx} />
                                                        </div>
                                                    </article>
                                                </React.Fragment>
                                            );
                                        })}
                                    </section>

                                    {/* pagintaion controls */}
                                    {(listingPage !== listingTotalPages) && (
                                        <div className='mt7 sm-mt7 md-mt9 px-gutter'>
                                            <div className='flex justify-center items-center'>
                                                <Button
                                                    action={() => { setListingPage(listingPage + 1); }}
                                                    className='form-field-reset button button--loading'>
                                                    <span className='capitalise'>
                                                        {t('See More')}
                                                    </span>
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

/* =========================================================================  */
/* Typing and exports */
/* =========================================================================  */

const propTypes = {
    sideTitle: PropTypes.string,
    title    : PropTypes.string,
};

ExploreJobAjax.propTypes = propTypes;

export { ExploreJobAjax };

export default ExploreJobAjax;
